import React, {useEffect, useMemo, useRef, useState} from "react";
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCol,
  IonContent,
  IonGrid,
  IonIcon,
  IonItem,
  IonItemDivider,
  IonLabel,
  IonLoading,
  IonPage,
  IonRow,
  IonToast
} from "@ionic/react";
import Header from "../components/Header";
import {ROLE} from "../../enums/ROLE";
import {AuthContext} from "../../providers/authContext";
import {cashOutline} from "ionicons/icons";
import {Calendar, CalendarValueType} from "primereact/calendar";
import yoneticiService from "../../services/YoneticiService";
import {formatDateToDDMMYYYY, logError, parseTurkishNumber} from "../../utils";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {TahsilatIzlemeListesi} from "../entities/TahsilatIzlemeListesi";
import {SelectItem} from "primereact/selectitem";
import {TahsilatTur} from "../entities/TahsilatTur";
import {Dropdown} from "primereact/dropdown";
import "./YoneticiTahsilatGozlem.css";
import {ColumnGroup} from "primereact/columngroup";
import {Row} from "primereact/row";

const YoneticiTahsilatGozlem: React.FC = () => {
  const { kurumBilgileri, logo } = React.useContext(AuthContext);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [showToastMessage, setShowToastMessage] = useState<string>();
  const [startDate, setStartDate] = useState<CalendarValueType>(new Date());
  const [endDate, setEndDate] = useState<CalendarValueType>(new Date());
  const [tahsilatListesi, setTahsilatListesi] = useState<TahsilatIzlemeListesi>();
  const [selectedTahsilatTur, setSelectedTahsilatTur] = useState(null);
  const [tahsilatTurSelectItems, setTahsilatTurSelectItems] = useState<SelectItem[]>([]);
  const [selectedTahsildar, setSelectedTahsildar] = useState(null);
  const [tahsildarSelectItems, setTahsildarSelectItems] = useState<SelectItem[]>([]);
  const [selectedGelirTur, setSelectedGelirTur] = useState(null);
  const [gelirTurSelectItems, setGelirTurSelectItems] = useState<SelectItem[]>([]);
  const [filtrelenmisTahsilatVerisi, setFiltrelenmisTahsilatVerisi] = useState(tahsilatListesi?.tahsilatListesi || []);
  const [filtrelenmisGelirTurVerisi, setFiltrelenmisGelirTurVerisi] = useState(tahsilatListesi?.gelirTurListesi || []);
  const tahsilatTable = useRef<DataTable>(null);
  const gelirTurTable = useRef<DataTable>(null);
  
  useEffect(() => {
    fetchToday();
  }, []);

  useEffect(() => {
    yoneticiService.getTahsilatTurListesi()
      .then((data: TahsilatTur[]) => {
        const items: SelectItem[] = Array.isArray(data) ? data.map(t => ({
          label: t.aciklama,
          value: t.tanim
        })) : [];
        setTahsilatTurSelectItems(items);
      })
      .catch((error) => {
        logError(error);
        setShowToastMessage(
          error.response ? error.response.data.message : error
        );
      });
  }, []);

  useEffect(() => {
    yoneticiService.getTahsildarListesi()
      .then((data: string[]) => {
        const items: SelectItem[] = Array.isArray(data) ? data.map(t => ({
          label: t,
          value: t
        })) : [];
        setTahsildarSelectItems(items);
      })
      .catch((error) => {
        logError(error);
        setShowToastMessage(
          error.response ? error.response.data.message : error
        );
      });
  }, []);

  useEffect(() => {
    yoneticiService.getGelirTurListesi()
      .then((data: string[]) => {
        const items: SelectItem[] = Array.isArray(data) ? data.map(t => ({
          label: t,
          value: t
        })) : [];
        setGelirTurSelectItems(items);
      })
      .catch((error) => {
        logError(error);
        setShowToastMessage(
          error.response ? error.response.data.message : error
        );
      });
  }, []);

  const fetchToday = () => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    setStartDate(start);
    setEndDate(end);
    fetchData(start, end);
  }

  const fetchData = (basTar?: Date | null, bitTar?: Date | null) => {
    if (!basTar || !bitTar) {
      setShowToastMessage("Başlangıç ve Bitiş Tarihlerini giriniz");
      return;
    }
    setShowLoading(true);
    yoneticiService
      .getTahsilatIzlemeListesi(formatDateToDDMMYYYY(basTar), formatDateToDDMMYYYY(bitTar))
      .then((data: TahsilatIzlemeListesi) => {
        const safeData = {
          tahsilatListesi: Array.isArray(data?.tahsilatListesi) ? data.tahsilatListesi : [],
          gelirTurListesi: Array.isArray(data?.gelirTurListesi) ? data.gelirTurListesi : [],
        };

        setTahsilatListesi(safeData);
        setFiltrelenmisTahsilatVerisi(safeData.tahsilatListesi);
        setFiltrelenmisGelirTurVerisi(safeData.gelirTurListesi);
      })
      .catch((error) => {
        logError(error);
        setShowToastMessage(
          error.response ? error.response.data.message : error
        );
      })
      .finally(() => setShowLoading(false));
  }

  const tahsilatTurFilter = <Dropdown value={selectedTahsilatTur} options={tahsilatTurSelectItems}
                                          onChange={(e) => {
                                            tahsilatTable.current?.filter(e.value, 'tur', 'equals');
                                            setSelectedTahsilatTur(e.value);
                                        }} placeholder="Hepsi"  className="p-column-filter" panelClassName="genis-panel" showClear style={{ width: '9rem' }} />
  const tahsildarFilter = <Dropdown value={selectedTahsildar} options={tahsildarSelectItems}
                                          onChange={(e) => {
                                            tahsilatTable.current?.filter(e.value, 'adiSoyadi', 'equals');
                                            setSelectedTahsildar(e.value);
                                        }} placeholder="Hepsi"  className="p-column-filter" showClear style={{ minWidth: '9rem' }} />
  const gelirTurFilter = <Dropdown value={selectedGelirTur} options={gelirTurSelectItems}
                                          onChange={(e) => {
                                            gelirTurTable.current?.filter(e.value, 'tur', 'equals');
                                            setSelectedGelirTur(e.value);
                                        }} placeholder="Hepsi"  className="p-column-filter" showClear style={{ minWidth: '9rem' }} />

  const tahsilatGenelToplam = useMemo(() => {
    const veri = Array.isArray(filtrelenmisTahsilatVerisi)
      ? filtrelenmisTahsilatVerisi
      : (Array.isArray(tahsilatListesi?.tahsilatListesi) ? tahsilatListesi!.tahsilatListesi : []);
    return veri.reduce((toplam, item) => toplam + (parseTurkishNumber(item.tutar) || 0), 0);
  }, [filtrelenmisTahsilatVerisi, tahsilatListesi]);

  const gelirTurGenelToplam = useMemo(() => {
    const veri = Array.isArray(filtrelenmisGelirTurVerisi)
      ? filtrelenmisGelirTurVerisi
      : (Array.isArray(tahsilatListesi?.gelirTurListesi) ? tahsilatListesi!.gelirTurListesi : []);
    return veri.reduce((toplam, item) => toplam + (parseTurkishNumber(item.tutar) || 0), 0);
  }, [filtrelenmisGelirTurVerisi, tahsilatListesi]);

  const tahsilatFooterGroup = (
    <ColumnGroup>
      <Row>
        <Column footer="GENEL TOPLAM" colSpan={2} footerStyle={{ textAlign: 'right' }} />
        <Column footer={tahsilatGenelToplam.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                footerStyle={{ textAlign: 'right', color: 'blue' }} />
        <Column footer="" />
      </Row>
    </ColumnGroup>
  );

  const gelirTurFooterGroup = (
    <ColumnGroup>
      <Row>
        <Column footer="GENEL TOPLAM" colSpan={2} footerStyle={{ textAlign: 'right' }} />
        <Column footer={gelirTurGenelToplam.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                footerStyle={{ textAlign: 'right', color: 'blue' }} />
      </Row>
    </ColumnGroup>
  );

  return (
    <IonPage>
      <Header
        title={kurumBilgileri?.kisaTanim}
        subtitle="Tahsilat Gözlem"
        logo={logo}
        roles={[ROLE.Yonetici]}
      />
      <IonContent>
        <IonLoading
          isOpen={showLoading}
          onDidDismiss={() => setShowLoading(false)}
          message={"Lütfen Bekleyin..."}
        />
        <IonToast
          isOpen={showToastMessage !== undefined}
          onDidDismiss={() => setShowToastMessage(undefined)}
          message={showToastMessage}
          duration={5000}
          position="top"
        />
        <IonCard>
          <IonCardHeader>
            <IonItem color="light">
              <IonIcon icon={cashOutline} slot="start" />
              Vezne Tahsilat Toplamları
            </IonItem>
            <IonItem color="light">
              <IonGrid>
                <IonRow>
                  <IonCol size="5" sizeSm="3"><IonButton expand="block" onClick={() => fetchToday()}>Bugün</IonButton></IonCol>
                  <IonCol size="5" sizeSm="3"><IonButton expand="block" onClick={() => {
                    const yesterday = new Date();
                    yesterday.setDate(yesterday.getDate() - 1);
                    setStartDate(yesterday);
                    setEndDate(yesterday);
                    fetchData(yesterday, yesterday);
                  }} color="medium">Dün</IonButton></IonCol>
                </IonRow>
                <IonRow className="ion-align-items-center">
                  <IonCol size="5" sizeSm="3"><IonLabel>Başlangıç Tarihi	</IonLabel></IonCol>
                  <IonCol size="5" sizeSm="3">
                    <Calendar id="startDate" locale="tr"
                              dateFormat="dd.mm.yy"
                              value={startDate}
                              onChange={(e) => {
                                setStartDate(e.value);
                                fetchData(e.value as Date | null, endDate as Date | null);
                              }} showIcon />
                  </IonCol>
                </IonRow>
                <IonRow className="ion-align-items-center">
                  <IonCol size="5" sizeSm="3"><IonLabel>Bitiş Tarihi	</IonLabel></IonCol>
                  <IonCol size="5" sizeSm="3">
                    <Calendar id="endDate"
                              dateFormat="dd.mm.yy"
                              value={endDate}
                              onChange={(e) => {
                                setEndDate(e.value);
                                fetchData(startDate as Date | null, e.value as Date | null);
                              }} showIcon />
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonItem>
          </IonCardHeader>
          <IonCardContent>
            <DataTable ref={tahsilatTable} value={tahsilatListesi?.tahsilatListesi || []}
                       className="p-datatable-sm tahsilat-table"
                       footerColumnGroup={tahsilatFooterGroup}
                       onValueChange={(e) => setFiltrelenmisTahsilatVerisi(e)}
                       stripedRows filterDisplay="row"
                       scrollable showGridlines
                       scrollDirection="horizontal"
                       style={{ minWidth: '100%' }}
                       emptyMessage="Tabloda herhangi bir veri mevcut değil">
              <Column field="tarih" header="TARİH" headerClassName="ion-justify-content-center"
                      bodyClassName="ion-justify-content-center mavi-font" />
              <Column field="tur" header="TÜR"
                      filter
                      filterElement={tahsilatTurFilter}
                      showFilterMenu={false}
                      showFilterMatchModes={false}
                      showClearButton={false} headerClassName="ion-justify-content-center" bodyClassName="ion-justify-content-center" />
              <Column field="tutar" header="TOPLAM TUTAR" headerClassName="ion-justify-content-center"
                      bodyClassName="ion-justify-content-end mavi-font" />
              <Column field="adiSoyadi" header="VEZNE"
                      filter
                      filterElement={tahsildarFilter}
                      showFilterMenu={false}
                      showFilterMatchModes={false}
                      showClearButton={false} />
            </DataTable>
            <IonItemDivider />
            <DataTable ref={gelirTurTable} value={tahsilatListesi?.gelirTurListesi || []}
                       className="p-datatable-sm"
                       footerColumnGroup={gelirTurFooterGroup}
                       onValueChange={(e) => setFiltrelenmisGelirTurVerisi(e)}
                       stripedRows filterDisplay="row"
                       scrollable showGridlines
                       scrollDirection="horizontal"
                       style={{ minWidth: '100%' }}
                       emptyMessage="Tabloda herhangi bir veri mevcut değil">
              <Column field="tarih" header="TARİH" headerClassName="ion-justify-content-center"
                      bodyClassName="ion-justify-content-center mavi-font" />
              <Column field="tur" header="GELİR TÜRÜ"
                      filter
                      filterElement={gelirTurFilter}
                      showFilterMenu={false}
                      showFilterMatchModes={false} headerClassName="ion-justify-content-center"
                      showClearButton={false} bodyClassName="ion-justify-content-center"/>
              <Column field="tutar" header="TOPLAM TUTAR"
                      headerClassName="ion-justify-content-center" bodyClassName="ion-justify-content-end mavi-font" />
            </DataTable>
          </IonCardContent>
        </IonCard>
      </IonContent>
    </IonPage>
  )
};

export default YoneticiTahsilatGozlem;
